import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'

// Q22 voting response order (exclude Unsure)
const VOTING_RESPONSE_ORDER = ['Yes', 'Not eligible', 'No']
const VOTING_EXCLUDED = ['Unsure']

// Voting colors: Yes = blue (affirmative), No = red (negative), Not eligible = gray
const VOTING_COLORS: Record<string, string> = {
  'Yes': '#4e79a7',       // blue - affirmative
  'No': '#e15759',        // red - negative
  'Not eligible': '#bab0ac', // gray - neutral
}

// Party order: Liberal, Conservative, Bloc, NDP, Green, then others
// NOTE: Data uses curly apostrophe (U+2019) - must match exactly
const PARTY_ORDER = [
  'Liberal Party of Canada',
  'Conservative Party of Canada',
  'Bloc Québécois',
  'New Democratic Party (NDP)',
  'Green Party of Canada',
  "People's Party of Canada",
  'Other',
  "I didn\u2019t vote",
  'Not eligible',
]

// Responses to exclude from display (non-substantive answers)
// NOTE: Data uses curly apostrophe (U+2019) not straight (') - must match exactly
const EXCLUDED_RESPONSES = [
  "Not sure/ I don\u2019t recall",
  "I didn\u2019t vote",
  "Not eligible",
]

// Legend labels (main parties only)
const LEGEND_LABELS = [
  'Liberal Party of Canada',
  'Conservative Party of Canada',
  'Bloc Québécois',
  'New Democratic Party (NDP)',
  'Green Party of Canada',
  "People's Party of Canada",
  'Other',
]

// Party colors (official brand colors)
// NOTE: Keys use curly apostrophe (U+2019) to match data
const PARTY_COLORS: Record<string, string> = {
  'Liberal Party of Canada': '#d71920',      // Liberal red
  'Conservative Party of Canada': '#1a4782', // Conservative blue
  'Bloc Québécois': '#33b2cc',               // Bloc light blue
  'New Democratic Party (NDP)': '#f37021',   // NDP orange
  'Green Party of Canada': '#3d9b35',        // Green
  "People's Party of Canada": '#5c3d7a',     // PPC purple
  'Other': '#9ca3af',                        // gray
  "I didn\u2019t vote": '#d1d5db',                // light gray
  "Not sure/ I don\u2019t recall": '#e5e7eb',     // lighter gray
  'Not eligible': '#f3f4f6',                 // very light gray
}

// Sort responses according to party order and filter out excluded responses
const sortPartyResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses]
    .filter(r => !EXCLUDED_RESPONSES.includes(r.label))
    .sort((a, b) => {
      const aIdx = PARTY_ORDER.indexOf(a.label)
      const bIdx = PARTY_ORDER.indexOf(b.label)
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
}

// Color getter for parties
const getPartyColor = (label: string) => PARTY_COLORS[label] || '#9ca3af'

export function PartySupport() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  const votingQuestion = data?.questions['Voted_2025Fed']
  const partyQuestion = data?.questions['PartySupport']

  // Sort voting responses helper (filter out Unsure)
  const sortVotingResponses = (responses: Array<{ label: string; percentage: number }>) => {
    return [...responses]
      .filter(r => !VOTING_EXCLUDED.includes(r.label))
      .sort((a, b) => {
        const aIdx = VOTING_RESPONSE_ORDER.indexOf(a.label)
        const bIdx = VOTING_RESPONSE_ORDER.indexOf(b.label)
        if (aIdx === -1) return 1
        if (bIdx === -1) return -1
        return aIdx - bIdx
      })
  }

  // Color getter for voting
  const getVotingColor = (label: string) => VOTING_COLORS[label] || '#bab0ac'

  // Voting question rows
  const votingRows = breakValues.map(value => {
    const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
    let questionData = votingQuestion?.data[filterKey]
    if (!questionData && selectedProvince) {
      questionData = votingQuestion?.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
    }
    return { value, filterKey, data: questionData }
  }).filter(row => row.data)

  // Party support rows
  const rows = breakValues.map(value => {
    const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
    let questionData = partyQuestion?.data[filterKey]
    if (!questionData && selectedProvince) {
      questionData = partyQuestion?.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
    }
    return { value, filterKey, data: questionData }
  }).filter(row => row.data)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Voter Intention</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      {/* Q22: Voting question */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Did you vote in the previous Canadian federal election on April 30, 2025?
        </p>

        {/* Legend for voting */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {VOTING_RESPONSE_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getVotingColor(label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : votingRows.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400">No data available</p>
          ) : (
            votingRows.map(row => (
              <div key={row.filterKey} className="flex items-center gap-2">
                <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                  {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                </div>
                <div className="flex-1">
                  <HorizontalStackedBar
                    segments={sortVotingResponses(row.data!.responses)}
                    scaleType="demographic"
                    height={isBreakActive ? 24 : 28}
                    showLabels={false}
                    normalize={true}
                    colorGetter={getVotingColor}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Q23: Party support question */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Which party did you support?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getPartyColor(label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400">No data available</p>
          ) : (
            rows.map(row => (
              <div key={row.filterKey} className="flex items-center gap-2">
                <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                  {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                </div>
                <div className="flex-1">
                  <HorizontalStackedBar
                    segments={sortPartyResponses(row.data!.responses)}
                    scaleType="demographic"
                    height={isBreakActive ? 24 : 28}
                    showLabels={false}
                    normalize={true}
                    colorGetter={(label) => getPartyColor(label)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
